import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Search } from "lucide-react";
import { fetch as fetchUsers } from "services/user-select.service";

/**
 * Pick several of the company's employees — the Tailwind counterpart of
 * features/multi-user-select (which is Polaris and does not belong in the
 * register's new UI).
 *
 * Same source and the same exclusions: `users/fetch` with `isUserGuest=false`
 * (no guests), plus this component's own client-side filter of anyone
 * disabled in this company. No `onlyActivated`, deliberately: (Dokobit)
 * signing happens on Dokobit's own page from an email link and needs no ERP
 * login, so somebody who has never activated their account still has to be
 * offered. The list is fetched once and filtered here — the server's own
 * text search only starts at four characters.
 */

type Props = {
  companyId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  label: string;
  disabled?: boolean;
};

type Option = { id: string; name: string; meta: string };

export default function InvoiceDocEmployeeMultiSelect({ companyId, value, onChange, label, disabled = false }: Props) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Option[]>([]);
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const data = await fetchUsers(companyId, { textSearch: "" }, false);
        if (cancelled) return;
        const rows: any[] = (data && data.usersCollection) || [];
        setOptions(
          rows
            .filter((row) => row && row.id && row.isDisabled !== true)
            .map((row) => ({
              id: String(row.id),
              name: row.name || row.email || String(row.id),
              meta: [row.position, row.email && row.email !== row.name ? row.email : null]
                .filter(Boolean)
                .join(" · "),
            }))
            .sort((a, b) => a.name.localeCompare(b.name, "lt"))
        );
        setStatus("success");
      } catch (e) {
        if (cancelled) return;
        setOptions([]);
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) => option.name.toLowerCase().includes(needle) || option.meta.toLowerCase().includes(needle)
    );
  }, [options, query]);

  const toggle = (id: string) => {
    if (disabled) return;
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{label}</span>
        {value.length > 0 && (
          <span className="text-xs text-gray-500">
            {t("invoice_inbox_sign_selected_count", { defaultValue: "Selected: {{count}}", count: value.length })}
          </span>
        )}
      </div>
      <label className="relative mt-1 block">
        <Search aria-hidden className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="search"
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("invoice_inbox_sign_search_employees", { defaultValue: "Search employees" })}
          aria-label={t("invoice_inbox_sign_search_employees", { defaultValue: "Search employees" })}
          className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-base md:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </label>
      <div
        role="group"
        aria-label={label}
        className="mt-2 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200"
      >
        {status === "loading" ? (
          <div className="px-3 py-2 text-sm text-gray-500">{t("loading", { defaultValue: "Loading…" })}</div>
        ) : status === "error" ? (
          <div className="px-3 py-2 text-sm text-red-700">
            {t("fetch_error", { defaultValue: "Could not load the list." })}
          </div>
        ) : !visible.length ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            {t("invoice_inbox_merge_no_results", { defaultValue: "Nothing found" })}
          </div>
        ) : (
          visible.map((option) => (
            <label
              key={option.id}
              className={clsx(
                "flex items-start gap-2 px-3 py-2 text-sm max-md:py-3",
                disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer hover:bg-gray-50"
              )}
            >
              <input
                type="checkbox"
                checked={value.includes(option.id)}
                disabled={disabled}
                onChange={() => toggle(option.id)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="min-w-0">
                <span className="block truncate text-gray-900">{option.name}</span>
                {option.meta && <span className="block truncate text-xs text-gray-500">{option.meta}</span>}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
